/**
 * Viewport culling utilities for Konva canvas performance.
 * Skips rendering elements that are far outside the visible stage area
 * to keep the canvas responsive with many elements.
 *
 * Uses conservative margins to avoid popping while panning/zooming.
 */

/**
 * Check if an element's bounding box intersects the expanded viewport.
 * @param {Object} element - Element with position/size data
 * @param {string} elementType - One of: 'Rect', 'Circle', 'Image', 'Text', 'Arrow', 'Line', 'DoubleSidedArrow'
 * @param {Object} stage - Konva stage (or stage ref) with width(), height()
 * @param {number} margin - Extra pixels around viewport (default 400 for pan/zoom safety)
 * @returns {boolean} true if element is in or near viewport (should render)
 */
export function isElementInViewport(element, elementType, stage, margin = 400) {
  if (!element || !stage) return true;

  const stageWidth = stage.width?.() ?? stage.attrs?.width ?? 0;
  const stageHeight = stage.height?.() ?? stage.attrs?.height ?? 0;
  if (!stageWidth || !stageHeight) return true;

  let left, top, right, bottom;

  switch (elementType) {
    case "Rect":
    case "Rectangle": {
      const x = element.x ?? 0;
      const y = element.y ?? 0;
      const w = element.width ?? 0;
      const h = element.height ?? 0;
      left = x;
      top = y;
      right = x + w;
      bottom = y + h;
      break;
    }
    case "Circle": {
      const x = element.x ?? 0;
      const y = element.y ?? 0;
      const rX = element.radius ?? element.radiusX ?? 0;
      const rY = element.radius ?? element.radiusY ?? 0;
      const r = Math.max(rX, rY);
      left = x - r;
      top = y - r;
      right = x + r;
      bottom = y + r;
      break;
    }
    case "Image": {
      const x = element.x ?? 0;
      const y = element.y ?? 0;
      const w = element.width ?? 0;
      const h = element.height ?? 0;
      left = x - w / 2;
      top = y - h / 2;
      right = left + w;
      bottom = top + h;
      break;
    }
    case "Text": {
      const x = element.x ?? 0;
      const y = element.y ?? 0;
      const w = element.width ?? 0;
      const h = element.height ?? 0;
      left = x;
      top = y;
      right = x + w;
      bottom = y + h;
      break;
    }
    case "Arrow":
    case "Line":
    case "DoubleSidedArrow": {
      const pts = element.points ?? [];
      const ox = element.x ?? 0;
      const oy = element.y ?? 0;
      if (pts.length < 2) {
        left = ox;
        top = oy;
        right = ox;
        bottom = oy;
      } else {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let i = 0; i < pts.length; i += 2) {
          const px = (pts[i] ?? 0) + ox;
          const py = (pts[i + 1] ?? 0) + oy;
          minX = Math.min(minX, px);
          minY = Math.min(minY, py);
          maxX = Math.max(maxX, px);
          maxY = Math.max(maxY, py);
        }
        left = minX;
        top = minY;
        right = maxX;
        bottom = maxY;
      }
      break;
    }
    default:
      return true;
  }

  const viewLeft = -margin;
  const viewTop = -margin;
  const viewRight = stageWidth + margin;
  const viewBottom = stageHeight + margin;

  if (right < viewLeft || left > viewRight || bottom < viewTop || top > viewBottom) {
    return false;
  }
  return true;
}
